import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Modal from "components/base/Modal";
import css from "../style.scss";
import copyicon from "assets/images/copyicon.svg";
import eyeoff from "assets/images/eye-off.svg";
import eyeon from "assets/images/eye-on.svg";
import { connect, useSelector, useDispatch } from "react-redux";
import { getBatchDialerApiKey, saveBatchDialerApiKey } from "data/search";
import { selectUserId, selectTeamMembers, selectTeamMembersLoading } from "data/user/selectors";
import LoadablePanel from "components/hoc/loadable/Panel";
import { getTeamMembers } from "data/user/actions";
import config from 'config';
import ToastNotification from "../../components/ToastNotification";
import { getBatchDialerIntegration, getCampaignsIntegration } from "data/user/actions";

const BatchDialer = ({ isOpen, onClose, modalType, text, setText, showPassword, togglePasswordVisibility, integrationId }) => {

  const {
    constants: {
      API: { PRICING_UPDATE_URL },
    },
  } = config;

  const [apiKey, setApiKey] = useState("");
  const [apiError, setApiError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [showCopyMsg, setShowCopyMsg] = useState(false);

  const dispatch = useDispatch();
  const userID = useSelector(selectUserId);
  const isTeamLoading = useSelector(selectTeamMembersLoading);

  useEffect(() => {
    if (isOpen && modalType === "view" && integrationId) {
      fetchIntegrationApiKey(integrationId);
    }
  }, [isOpen, modalType, integrationId]);

  useEffect(() => {
    if (isOpen && modalType === "connect") {
      setApiKey("");
      setIsConnected(false);
      setApiError("");
      setShowCopyMsg(false);
    }
  }, [isOpen, modalType]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      externalSystem: "BATCH_DIALER",
      description: "",
      apiKey: apiKey,
      parameters: {
        externalSystem: "BATCH_DIALER",
        allowFullAccessTeamMembersToPushToBatchDialer: isConnected
      }
    };

    try {
      const response = await fetch(`${PRICING_UPDATE_URL}/ps/integrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': window.appAuthToken
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 201) {
        dispatch(getBatchDialerIntegration(userID));
        dispatch(getCampaignsIntegration());
        setToastMessage("BatchDialer successfully connected");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        onClose();
        setApiKey(payload.apiKey);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setApiError(errorData.message || "Something went wrong");
      }
    } catch (e) {
      setApiError("Network error. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchIntegrationApiKey = async (id) => {
    try {
      const res = await fetch(`${PRICING_UPDATE_URL}/ps/integrations/${id}`, {
        headers: {
          "x-auth-token": window.appAuthToken
        }
      });
      if (res.ok) {
        const data = await res.json();
        setApiKey(data.apiKey || "");
      }
    } catch (err) {
      console.error("Error fetching API key:", err);
    }
  };

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(apiKey);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = apiKey;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setShowCopyMsg(true);
      setTimeout(() => setShowCopyMsg(false), 2000);

    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleInternalClose = () => {
    setApiKey("");
    setIsConnected(false);
    setApiError("");
    setShowCopyMsg(false);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        isCloseButton={false}
        uniqId="BatchDialerModal"
        width="auto"
        borderRadius="12px"
        modalClassName={css.batchDialerModal}
        className={classNames(css.clsAccPageModals, css.clsBillingModal)}
        onClose={onClose}
      >
        <div className={css.modalContainer}>
          <div className={css.headertxt}>
            <span>
              {modalType === "connect"
                ? "Connect to BatchDialer API"
                : modalType === "view"
                  ? "BatchDialer API Key"
                  : "Push to BatchDialer Team Permission"}
            </span>
          </div>
          <div className={css.instructionText}>
            {modalType === "connect" && (
              <>
                <span>
                  Go to BatchDialer{" "}
                  <span className={css.subtxt}>Settings &gt; Integrations</span> and
                  click the Integrate
                </span>
                <br />
                <span>button on the Propstream Card to generate your API key.</span>
              </>
            )}
          </div>
          {(modalType === "connect" || modalType === "view") && (
            <div className={css.inputWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setApiError("");
                }}
                placeholder="Enter API Key"
                readOnly={modalType === "view"}
                className={classNames(css.apiInput, {
                  [css.errorBorder]: apiError
                })}
              />
              <div className={css.iconGroup}>
                <div className={css.eyeIcon}>
                  <img
                    src={showPassword ? eyeon : eyeoff}
                    alt="toggle visibility"
                    onClick={togglePasswordVisibility}
                  />
                </div>
                {modalType === "view" && (
                  <div className={css.copyIcon} onClick={handleCopy}>
                    <img src={copyicon} alt="copyicon" />
                    <div className={css.copyTooltip}>Copy API Key</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {apiError && (
            <span className={css.errorMsg}>{apiError}</span>
          )}
          {showCopyMsg && (
            <span className={css.copySuccessMsg}>
              API key copied successfully.
            </span>
          )}

          {modalType === "connect" && (
            <>
              <div className={css.checkboxWrapper}>
                <label className={css.checkboxContainer}>
                  <input type="checkbox" checked={isConnected} onChange={(e) => setIsConnected(e.target.checked)} />
                  <span className={css.checkmark}>
                    <svg viewBox="0 0 24 24" className={css.svgIcon}>
                      <path
                        d="M5 13L9.5 17.5L19 7"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </label>
                <span className={css.checkboxtxt}>
                  Allow full-access team members to add numbers to BatchDialer
                </span>
              </div>
            </>
          )}

          <div className={css.buttonWrapper}>
            {modalType === "connect" && (
              <>
                <button className={css.cancelBtn} onClick={handleInternalClose}>
                  <span className={css.cancelBtnLabel}>Cancel</span>
                </button>

                <button
                  className={classNames(css.connectModalBtn, {
                    [css.disabledBtn]: apiKey.trim() === ""  || isSubmitting,
                    [css.noClick]: isSubmitting,
                  })}
                  disabled={apiKey.trim() === ""  || isSubmitting}
                  onClick={handleSubmit}
                >
                  <span className={css.connectBtnLabel}>Connect</span>
                </button>
              </>
            )}
            {modalType === "view" && (
              <button className={css.connectModalBtn} onClick={onClose}>
                <span className={css.connectBtnLabel}>Ok</span>
              </button>
            )}
          </div>
        </div>
      </Modal>
      <ToastNotification message={toastMessage} show={showToast} />
    </>
  );
};

export default BatchDialer;